import React, {useEffect, useState, useRef, useMemo} from 'react';
import {GridRowClassNameParams, GridSortModel} from '@mui/x-data-grid';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import BarChartCell from './BarChartCell';
import {CustomDataTableProps} from './HostingVolumeModel';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {fetchHostingVolumeReportDetails} from '../../../api/ReportingManagement';
import {
  CUSTOM_REPORT_COL_HEIGHT,
  failedToLoadStatusCodes,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_50_ROW,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';

const HostingCustodiansTable: React.FC<CustomDataTableProps> = ({
  columns,
  title,
  setSelectedRowData,
  apiData,
}) => {
  const [currentPageGrid, setCurrentPageGrid] = useState<number>(1);
  const [itemsPerPageGrid, setItemsPerPageGrid] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_50_ROW
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [gridRows, setGridRows] = useState<any[]>([]);
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(
    (tableContainerRef.current?.offsetHeight ?? 500) - CUSTOM_REPORT_COL_HEIGHT
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isFetchReportDetailsInitial, setIsFetchReportDetailsInitial] =
    useState(true);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  // when click outside the grid then selected row should reset
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target as Node)
      ) {
        setSelectedRowId(null);
        setSelectedRowData(apiData);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [apiData]);

  /**
   * To get datagrid height
   */
  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        setSize(
          tableContainerRef.current.offsetHeight - CUSTOM_REPORT_COL_HEIGHT
        );
      }
    };
    window.addEventListener('resize', updateHeight);
    updateHeight();

    return () => window.removeEventListener('resize', updateHeight);
  });

  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );

  const onRowClick = (rowData: any) => {
    const clickedRowId = rowData.id;
    if (clickedRowId === selectedRowId) {
      setSelectedRowId(null);
      setSelectedRowData(apiData);
    } else {
      setSelectedRowId(clickedRowId);
      setSelectedRowData(rowData);
    }
  };

  const fetchReportDetails = async () => {
    try {
      setIsFailedToLoadContent(false);
      const result = await fetchHostingVolumeReportDetails(
        clientId,
        projectId,
        workspaceId,
        currentPageGrid,
        itemsPerPageGrid
      );
      const data = result?.data?.data.map((item: any, index: any) => {
        return {...item, id: index};
      });

      setTotalCount(result?.data?.meta?.pagination?.totalItems);
      if (isFetchReportDetailsInitial) {
        if (
          result?.data?.meta?.pagination?.totalItems <=
          TABLE_PER_PAGE_DEFAULT_50_ROW
        ) {
          setGridRows(data || []);
        }
      } else {
        setGridRows(data || []);
      }
    } catch (e: any) {
      if (failedToLoadStatusCodes.includes(e.status))
        setIsFailedToLoadContent(true);
    }
  };

  useEffect(() => {
    if (!isFetchReportDetailsInitial && clientId && projectId && workspaceId) {
      fetchReportDetails();
    }
  }, [
    clientId,
    projectId,
    workspaceId,
    currentPageGrid,
    itemsPerPageGrid,
    isFetchReportDetailsInitial,
  ]);

  useEffect(() => {
    if (clientId && projectId && workspaceId && isFetchReportDetailsInitial) {
      fetchHostingVolumeReportDetails(clientId, projectId, workspaceId, 1, 1)
        .then((result) => {
          const total = result?.data?.meta?.pagination?.totalItems || 0;
          setTotalCount(total);
          const defaultPageSize =
            total > TABLE_PER_PAGE_DEFAULT_50_ROW
              ? TABLE_PER_PAGE_DEFAULT_50_ROW
              : TABLE_PER_PAGE_DEFAULT_NO_ROW;
          setItemsPerPageGrid(defaultPageSize);
          setIsFetchReportDetailsInitial(false);
        })
        .catch((e: any) => {
          if (failedToLoadStatusCodes.includes(e.status))
            setIsFailedToLoadContent(true);
        });
    }
  }, [clientId, projectId, workspaceId]);

  const gridColumns = useMemo(() => {
    return columns.map((column) => {
      // calculating max value based on maximum value of row
      const maxValue = column.isChart
        ? Math.max(...gridRows.map((row) => Number(row[column.field]) || 0))
        : 0;

      return {
        ...column,
        renderCell: (params: any) => {
          return column.isChart ? (
            <div style={{position: 'relative', overflow: 'visible'}}>
              <BarChartCell
                value={Number(params.value) || 0}
                maxValue={maxValue}
                name={params.field}
              />
            </div>
          ) : (
            <span>{params.value}</span>
          );
        },
      };
    });
  }, [columns, gridRows]);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const getRowClassName = (params: GridRowClassNameParams) => {
    return params.row.id === selectedRowId ? 'selected-row' : '';
  };

  return (
    <div className="hostingvolume-table">
      <Typography
        variant="h2"
        component="h2"
        className="typo-text subhead2_size custom-bg-heading title"
      >
        {title}
      </Typography>
      <Box ref={tableContainerRef}>
        <AurDataGrid
          rows={gridRows}
          getRowId={(row) => row.id}
          columns={gridColumns}
          visibleToolTip={true}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
          checkBoxVisible={false}
          entityType={''}
          pointer={true}
          currentPage={currentPageGrid}
          setCurrentPage={setCurrentPageGrid}
          itemsPerPage={itemsPerPageGrid}
          setItemsPerPage={setItemsPerPageGrid}
          sortModel={sortModel}
          setSortModel={onSetSortModel}
          onRowClick={(params) => onRowClick(params.row)}
          noRowsOverlay={
            isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
          }
          onRefresh={fetchReportDetails}
          minItemForPagination={0}
          containerHeight={size}
          totalRowCount={totalCount}
          data-testid="hosting-custodians-table"
          className="hostingCustodiansTable"
          disableRowSelectionOnClick
          enableRowClickOnEnterPress
          getRowClassName={getRowClassName}
          trackPaginationActionName={t('gaTracking.global.reportPagination')}
        />
      </Box>
    </div>
  );
};

export default HostingCustodiansTable;
